import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon, 
  ShieldCheckIcon, 
  ChartBarIcon,
  Cog6ToothIcon,
  BoltIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { riskApi } from '@/services/api';
import RiskMetricsCard from '@/components/Risk/RiskMetricsCard';
import EmergencyControls from '@/components/Risk/EmergencyControls';
import RiskSettings from '@/components/Risk/RiskSettings';
import RiskEventsTable from '@/components/Risk/RiskEventsTable';

interface RiskDashboard {
  latestSnapshot: any;
  unresolvedEvents: any[];
  activeEmergencyStops: any[];
  riskProfile: any;
  monitoringStatus: {
    isMonitoring: boolean;
    lastUpdate: string;
    riskLevel: string;
    violationsCount: number;
  };
}

interface RiskAnalysis {
  riskScore: number;
  riskLevel: string;
  exposurePct: number;
  largestPositionPct: number;
  violations: any[];
  riskBreakdown: any;
}

const RiskDashboard: React.FC = () => {
  const [monitoring, setMonitoring] = useState<RiskDashboard | null>(null);
  const [analysis, setAnalysis] = useState<RiskAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-refresh interval (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Initial load
  useEffect(() => {
    handleRefresh();
  }, []);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);

      console.log('[RISK_DASHBOARD] Starting API refresh at:', new Date().toISOString());
      const startTime = Date.now();

      // STEP 1: Get risk monitoring status (required for analysis)
      const monitoringStartTime = Date.now();
      const monitoringResponse = await riskApi.getMonitoring();
      const monitoringTime = Date.now() - monitoringStartTime;
      console.log(`[RISK_DASHBOARD] Risk monitoring loaded in ${monitoringTime}ms`);
      
      // Backend returns data directly in response body, not wrapped in .monitoring
      const monitoringData = (monitoringResponse as any).monitoring || monitoringResponse;
      setMonitoring(monitoringData);

      // STEP 2: Conditional portfolio analysis (depends on snapshot data)
      let analysisData = null;
      if (monitoringData?.latestSnapshot) {
        const analysisStartTime = Date.now();
        const snapshot = monitoringData.latestSnapshot;
        const portfolioData = {
          totalValue: snapshot.total_value,
          totalCash: snapshot.total_cash,
          totalPositions: snapshot.total_positions,
          unrealizedPnl: snapshot.unrealized_pnl,
          realizedPnlToday: snapshot.realized_pnl_today,
          positions: JSON.parse(snapshot.positions || '[]')
        };

        const analysisResponse = await riskApi.analyzePortfolio(portfolioData);
        const analysisTime = Date.now() - analysisStartTime;
        console.log(`[RISK_DASHBOARD] Portfolio analysis completed in ${analysisTime}ms`);
        
        // Backend returns data directly in response body, not wrapped in .analysis
        analysisData = (analysisResponse as any).analysis || analysisResponse;
        setAnalysis(analysisData);
      } else {
        console.log('[RISK_DASHBOARD] No snapshot data available, skipping portfolio analysis');
        setAnalysis(null);
      }

      const totalTime = Date.now() - startTime;
      console.log(`[RISK_DASHBOARD] Complete refresh finished in ${totalTime}ms`);

    } catch (err: any) {
      console.error('[RISK_DASHBOARD] Failed to refresh risk dashboard:', err);
      setError(err.message || 'Failed to load risk monitoring data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getRiskLevelColor = (level: string) => {
    switch (level?.toUpperCase()) {
      case 'LOW': return 'text-success-custom bg-success-subtle';
      case 'MEDIUM': return 'text-warning-custom bg-warning-subtle';
      case 'HIGH': return 'text-warning-custom bg-warning-subtle';
      case 'CRITICAL': return 'text-error-custom bg-error-subtle';
      default: return 'text-secondary-custom bg-secondary-custom';
    }
  };

  const getRiskIcon = (level: string) => {
    switch (level?.toUpperCase()) {
      case 'LOW': return <ShieldCheckIcon className="h-5 w-5 text-green-600" />;
      case 'MEDIUM': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
      case 'HIGH': return <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />;
      case 'CRITICAL': return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
      default: return <ShieldCheckIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number, decimals: number = 1) => {
    return `${value.toFixed(decimals)}%`;
  };

  if (loading && !monitoring) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-2 text-secondary-custom">Loading risk monitoring...</span>
      </div>
    );
  }

  const currentRiskLevel = analysis?.riskLevel || monitoring?.monitoringStatus?.riskLevel || 'UNKNOWN';
  const riskScore = analysis?.riskScore || monitoring?.latestSnapshot?.risk_score || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-heading-1 font-bold text-primary-custom">Risk Management</h1>
          <p className="text-body text-secondary-custom">Portfolio risk monitoring and emergency controls</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-body font-medium text-gray-700 card hover-bg-secondary-custom disabled:opacity-50"
          >
            <ClockIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-body font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Cog6ToothIcon className="h-4 w-4 mr-2" />
            Risk Settings
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-body font-medium text-red-800">Error Loading Risk Data</h3>
              <div className="mt-2 text-body text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Status Overview */}
      <div className="card shadow rounded-lg">
        <div className="px-6 py-4 border-b border-primary-custom">
          <div className="flex items-center justify-between">
            <h2 className="text-heading-2 font-medium text-primary-custom">Current Risk Status</h2>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-body font-medium ${getRiskLevelColor(currentRiskLevel)}`}>
              {getRiskIcon(currentRiskLevel)}
              <span className="ml-2">{currentRiskLevel} RISK</span>
            </div>
          </div>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <dt className="text-body font-medium text-secondary-custom">Risk Score</dt>
              <dd className="mt-1 text-data-large font-semibold text-primary-custom">
                {riskScore}/100
              </dd>
              <div className="mt-2 w-full bg-secondary-custom rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    riskScore >= 70 ? 'bg-red-500' :
                    riskScore >= 50 ? 'bg-orange-500' :
                    riskScore >= 30 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(riskScore, 100)}%` }}
                ></div>
              </div>
            </div>
            <div>
              <dt className="text-body font-medium text-secondary-custom">Portfolio Value</dt>
              <dd className="mt-1 text-data-large font-semibold text-primary-custom">
                {formatCurrency(monitoring?.latestSnapshot?.total_value || 0)}
              </dd>
              <div className="mt-2 text-body text-secondary-custom">
                Cash: {formatCurrency(monitoring?.latestSnapshot?.total_cash || 0)}
              </div>
            </div>
            <div>
              <dt className="text-body font-medium text-secondary-custom">Portfolio Exposure</dt>
              <dd className="mt-1 text-data-large font-semibold text-primary-custom">
                {formatPercentage(analysis?.exposurePct || monitoring?.latestSnapshot?.portfolio_exposure_pct || 0)}
              </dd>
              <div className="mt-2 text-body text-secondary-custom">
                of total portfolio
              </div>
            </div>
            <div>
              <dt className="text-body font-medium text-secondary-custom">Violations</dt>
              <dd className="mt-1 text-data-large font-semibold text-primary-custom">
                {monitoring?.monitoringStatus?.violationsCount || 0}
              </dd>
              <div className="mt-2 text-body text-secondary-custom">
                active violations
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Stops Alert */}
      {monitoring?.activeEmergencyStops && monitoring.activeEmergencyStops.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <BoltIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-body font-medium text-red-800">Active Emergency Stops</h3>
              <div className="mt-2 text-body text-red-700">
                {monitoring.activeEmergencyStops.length} emergency stop(s) currently active
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Risk Metrics */}
        <div className="lg:col-span-2 space-y-6">
          {/* Risk Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RiskMetricsCard
              title="Position Concentration"
              value={formatPercentage(analysis?.largestPositionPct || monitoring?.latestSnapshot?.largest_position_pct || 0)}
              subtitle={`Largest position: ${monitoring?.latestSnapshot?.largest_position_symbol || 'N/A'}`}
              status={(analysis?.largestPositionPct || 0) > 30 ? 'warning' : 'good'}
              icon={<ChartBarIcon className="h-6 w-6" />}
            />
            <RiskMetricsCard
              title="Daily P&L"
              value={formatCurrency(monitoring?.latestSnapshot?.realized_pnl_today || 0)}
              subtitle="Realized today"
              status={
                (monitoring?.latestSnapshot?.realized_pnl_today || 0) < 0 ? 'warning' :
                (monitoring?.latestSnapshot?.realized_pnl_today || 0) > 0 ? 'good' : 'neutral'
              }
              icon={<ChartBarIcon className="h-6 w-6" />}
            />
          </div>

          {/* Risk Events Table */}
          <RiskEventsTable 
            events={monitoring?.unresolvedEvents || []} 
            onRefresh={handleRefresh}
          />
        </div>

        {/* Right Column: Emergency Controls */}
        <div>
          <EmergencyControls 
            activeStops={monitoring?.activeEmergencyStops || []}
            onEmergencyStop={handleRefresh}
            onLiftStop={handleRefresh}
          />
        </div>
      </div>

      {/* Risk Settings Modal */}
      {showSettings && (
        <RiskSettings 
          riskProfile={monitoring?.riskProfile}
          onClose={() => setShowSettings(false)}
          onSave={handleRefresh}
        />
      )}

      {/* Auto-refresh indicator */}
      {monitoring?.monitoringStatus?.lastUpdate && (
        <div className="text-caption text-secondary-custom text-center">
          Last updated: {new Date(monitoring.monitoringStatus.lastUpdate).toLocaleString()}
          <span className="ml-2">• Auto-refresh every 30 seconds</span>
        </div>
      )}
    </div>
  );
};

export default RiskDashboard; 